import React, { useCallback, useEffect, useState } from 'react';
import {
  downloadOutboxDeliveryReliabilityReportCsv,
  fetchOutboxDeliveryReliabilityReport,
  type OutboxDeliveryReliabilityReportParams,
  type OutboxDeliveryReliabilityReportResponse,
} from '../api/reports';
import {
  collapseEmptyHistory,
  outboxRollupHasActivity,
} from '../components/reports/collapseEmptyHistory';
import { ReportGeneratedHeader } from '../components/reports/ReportGeneratedHeader';
import { ShowEmptyDaysToggle } from '../components/reports/ShowEmptyDaysToggle';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

export const ReportsOutboxReliabilityPage: React.FC = () => {
  const [report, setReport] = useState<OutboxDeliveryReliabilityReportResponse | null>(null);
  const [view, setView] = useState<'daily' | 'weekly'>('daily');
  const [days, setDays] = useState('14');
  const [channel, setChannel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmptyDays, setShowEmptyDays] = useState(false);

  const buildParams = useCallback((): OutboxDeliveryReliabilityReportParams => {
    const parsedDays = Number.parseInt(days, 10);
    return {
      view,
      days: Number.isNaN(parsedDays) || parsedDays < 14 ? 14 : parsedDays,
      channel: channel.trim() || undefined,
    };
  }, [view, days, channel]);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const nextReport = await fetchOutboxDeliveryReliabilityReport(buildParams());
      setReport(nextReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load outbox delivery reliability report');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    void loadReport();
  }, []);

  const handleApplyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadReport();
  };

  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      await downloadOutboxDeliveryReliabilityReportCsv(buildParams());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export outbox delivery report');
    } finally {
      setIsExporting(false);
    }
  };

  const rollupRows = report
    ? collapseEmptyHistory(report.rollups, {
        periodKey: (item) => item.period_start,
        periodEndKey: (item) => item.period_end,
        hasActivity: outboxRollupHasActivity,
        showEmptyDays,
        generatedAt: report.generated_at,
      })
    : [];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Reports: Outbox Delivery Reliability</h1>
        <p style={styles.subtitle}>
          Tracks outbox backlog, retries, dispatch lag percentiles, replay outcomes, and anomaly indicators over daily or weekly windows.
        </p>
      </div>

      <ReportGeneratedHeader
        generatedAt={report?.generated_at}
        onRefresh={() => {
          void loadReport();
        }}
        refreshing={isLoading}
      />

      <form style={styles.filtersRow} onSubmit={handleApplyFilters}>
        <label style={styles.filterLabel}>
          View
          <select value={view} onChange={(event) => setView(event.target.value as 'daily' | 'weekly')} style={styles.input}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </label>

        <label style={styles.filterLabel}>
          Window (days)
          <input type="number" min={14} max={90} value={days} onChange={(event) => setDays(event.target.value)} style={styles.input} />
        </label>

        <label style={styles.filterLabel}>
          Channel
          <input value={channel} onChange={(event) => setChannel(event.target.value)} style={styles.input} placeholder="shopify" />
        </label>

        <div style={styles.actions}>
          <button type="submit" style={styles.primaryButton} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Apply'}
          </button>
          <button type="button" style={styles.secondaryButton} onClick={handleExportCsv} disabled={isLoading || isExporting}>
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <div style={styles.summaryGrid}>
            <MetricCard label="Queued Volume" value={String(report.summary.queued_volume)} />
            <MetricCard label="Pending Items" value={String(report.summary.pending_count)} />
            <MetricCard label="Dispatch Failed" value={String(report.summary.dispatch_failed)} />
            <MetricCard label="Retry Exhaustions" value={String(report.summary.retry_exhaustions)} />
          </div>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Anomaly Indicators</h2>
            </div>
            <ul style={styles.metricList}>
              <li style={styles.metricListItem}>
                <span>Backlog acceleration</span>
                <strong>{report.anomaly_indicators.backlog_acceleration ? 'Yes' : 'No'}</strong>
              </li>
              <li style={styles.metricListItem}>
                <span>Stale queue growth</span>
                <strong>{report.anomaly_indicators.stale_queue_growth ? 'Yes' : 'No'}</strong>
              </li>
            </ul>
          </section>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Delivery Rollups</h2>
              <ShowEmptyDaysToggle checked={showEmptyDays} onChange={setShowEmptyDays} />
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Period</th>
                    <th style={styles.th}>Channel</th>
                    <th style={styles.th}>Queued</th>
                    <th style={styles.th}>Pending</th>
                    <th style={styles.th}>Oldest Pending</th>
                    <th style={styles.th}>Dispatch Success</th>
                    <th style={styles.th}>Dispatch Failed</th>
                    <th style={styles.th}>Failure %</th>
                    <th style={styles.th}>Retry Total</th>
                    <th style={styles.th}>Retry Exhaustions</th>
                    <th style={styles.th}>Lag p50</th>
                    <th style={styles.th}>Lag p95</th>
                  </tr>
                </thead>
                <tbody>
                  {rollupRows.length === 0 && (
                    <tr>
                      <td colSpan={12} style={styles.emptyCell}>
                        {report.rollups.length === 0
                          ? 'No outbox rollup data is available for the selected filters.'
                          : 'No activity in this window.'}
                      </td>
                    </tr>
                  )}
                  {rollupRows.map((item) => (
                    <tr key={`${item.period_start}-${item.channel}`}>
                      <td style={styles.td}>{formatPeriod(item.period_start, item.period_end)}</td>
                      <td style={styles.tdStrong}>{item.channel}</td>
                      <td style={styles.td}>{item.queued_volume}</td>
                      <td style={styles.td}>{item.pending_count}</td>
                      <td style={styles.td}>{formatDuration(item.oldest_pending_age_seconds)}</td>
                      <td style={styles.td}>{item.dispatch_success}</td>
                      <td style={styles.td}>{item.dispatch_failed}</td>
                      <td style={styles.td}>{item.dispatch_failure_rate_pct.toFixed(2)}%</td>
                      <td style={styles.td}>{item.retry_total}</td>
                      <td style={styles.td}>{item.retry_exhaustions}</td>
                      <td style={styles.td}>{formatDuration(item.lag_p50_seconds)}</td>
                      <td style={styles.td}>{formatDuration(item.lag_p95_seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Replay Operations</h2>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Replay Key</th>
                    <th style={styles.th}>Channels</th>
                    <th style={styles.th}>Dry Run</th>
                    <th style={styles.th}>Entries</th>
                    <th style={styles.th}>Completed</th>
                    <th style={styles.th}>Failed</th>
                    <th style={styles.th}>Pending</th>
                    <th style={styles.th}>First Seen</th>
                    <th style={styles.th}>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {report.replay_operations.length === 0 && (
                    <tr>
                      <td colSpan={9} style={styles.emptyCell}>No replay operations were observed in this window.</td>
                    </tr>
                  )}
                  {report.replay_operations.map((item) => (
                    <tr key={item.replay_key}>
                      <td style={styles.tdStrong}>{item.replay_key}</td>
                      <td style={styles.td}>{item.channels.join(', ')}</td>
                      <td style={styles.td}>{item.is_dry_run ? 'Yes' : 'No'}</td>
                      <td style={styles.td}>{item.queue_entries}</td>
                      <td style={styles.td}>{item.completed}</td>
                      <td style={styles.td}>{item.failed}</td>
                      <td style={styles.td}>{item.pending}</td>
                      <td style={styles.td}>{formatTimestamp(item.first_seen_at)}</td>
                      <td style={styles.td}>{formatTimestamp(item.last_seen_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.metricCard}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.metricValue}>{value}</strong>
    </div>
  );
}

function formatDuration(value: number | null): string {
  if (value === null || value === undefined) {
    return '-';
  }
  const seconds = Math.floor(value);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function formatPeriod(start: string, end: string): string {
  return `${start.slice(0, 10)} to ${end.slice(0, 10)}`;
}

function formatTimestamp(value: string | null): string {
  return formatInstant(value, '-');
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.lg,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'end',
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  filterLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    minWidth: '150px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.md,
  },
  metricCard: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  metricLabel: {
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
  },
  metricValue: {
    color: colors.text,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
  },
  card: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.sm,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    margin: 0,
    color: colors.text,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
  },
  metricList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  metricListItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    color: colors.text,
    fontSize: typography.body.fontSize,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.body.fontSize,
  },
  tdStrong: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
  },
};
